import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class SquarespaceOrder extends ModelObject<SquarespaceOrder> {

    public version: number = 0;
    /**
    * The Squarespace unique identifier for this order.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public squarespaceOrderId: string = "";
    /**
    * The Squarespace unqiue sequential number for this order.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public squarespaceOrderNumber: string = "";
    /**
    * The date and time this order was created in Squarespace.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * The total number of items in the order.
    */
    public totalOrderQuantity: number = 0;
    /**
    * The total net value of the order including delivery.
    */
    public totalNetPrice: number = 0;
    private _brewmanOrderId: Guid | null = null;
    /**
    * The Brewman order id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanOrderId(): Guid | null { return this._brewmanOrderId }
    public set brewmanOrderId(value: Guid | null) {
      this._brewmanOrderId = value === undefined ? null : value;
    }
    /**
    * Error information associated with a failed import.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessage: string = "";
    private _squarespaceDocumentId: Guid | null = null;
    /**
    * Squarespace document id for this order. Used for payment and tax details.
    */
    public get squarespaceDocumentId(): Guid | null { return this._squarespaceDocumentId }
    public set squarespaceDocumentId(value: Guid | null) {
      this._squarespaceDocumentId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.squarespaceOrderId = jsonSerializedObject.squarespaceOrderId;
          this.squarespaceOrderNumber = jsonSerializedObject.squarespaceOrderNumber;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.totalOrderQuantity = jsonSerializedObject.totalOrderQuantity;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.brewmanOrderId = jsonSerializedObject.brewmanOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOrderId);
          this.errorMessage = jsonSerializedObject.errorMessage;
          this.squarespaceDocumentId = jsonSerializedObject.squarespaceDocumentId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.squarespaceDocumentId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.squarespaceOrderId = this.squarespaceOrderId;
        toRet.squarespaceOrderNumber = this.squarespaceOrderNumber;
        toRet.createdAt = this.createdAt.toString();
        toRet.totalOrderQuantity = this.totalOrderQuantity;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.brewmanOrderId = this.brewmanOrderId == null ? null : this.brewmanOrderId.toString();
        toRet.errorMessage = this.errorMessage;
        toRet.squarespaceDocumentId = this.squarespaceDocumentId == null ? null : this.squarespaceDocumentId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "brewmanOrderId", rule: (v: any) => v == null || !v.isEmpty() || "SquarespaceOrder.BrewmanOrderId cannot be empty"},
            {field: "squarespaceDocumentId", rule: (v: any) => v == null || !v.isEmpty() || "SquarespaceOrder.SquarespaceDocumentId cannot be empty"},
    ];
}


export class FilteredSquarespaceOrdersResult extends ModelObject<FilteredSquarespaceOrdersResult> {

    public matchingCount: number = 0;
    public squarespaceOrders: (SquarespaceOrder)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.squarespaceOrders = jsonSerializedObject.squarespaceOrders.map((v: any) => new SquarespaceOrder(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.squarespaceOrders = this.squarespaceOrders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "squarespaceOrders", rule: (v: any) => !!v || "squarespaceOrders is required"},
            {field: "squarespaceOrders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SquarespaceOrder[]).reduce((acc: Array<string>, curr: SquarespaceOrder) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class SquarespaceOrderFilter extends ModelObject<SquarespaceOrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _createdFromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) created at date
    */
    public get createdFromDate(): LocalDate | null { return this._createdFromDate }
    public set createdFromDate(value: LocalDate | null) {
      this._createdFromDate = value === undefined ? null : value;
    }
    private _createdToDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) created at date
    */
    public get createdToDate(): LocalDate | null { return this._createdToDate }
    public set createdToDate(value: LocalDate | null) {
      this._createdToDate = value === undefined ? null : value;
    }
    private _squarespaceOrderIds: readonly (string)[] | null = null;
    /**
    * Limit to specified squarespace order id
    */
    public get squarespaceOrderIds(): readonly (string)[] | null { return this._squarespaceOrderIds }
    public set squarespaceOrderIds(value: readonly (string)[] | null) {
      this._squarespaceOrderIds = value === undefined ? null : value;
    }
    private _squarespaceOrderNumbers: readonly (string)[] | null = null;
    /**
    * Limit to specified squarespace order number
    */
    public get squarespaceOrderNumbers(): readonly (string)[] | null { return this._squarespaceOrderNumbers }
    public set squarespaceOrderNumbers(value: readonly (string)[] | null) {
      this._squarespaceOrderNumbers = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.createdFromDate = jsonSerializedObject.createdFromDate == null ? null : LocalDate.parse(jsonSerializedObject.createdFromDate);
          this.createdToDate = jsonSerializedObject.createdToDate == null ? null : LocalDate.parse(jsonSerializedObject.createdToDate);
          this.squarespaceOrderIds = jsonSerializedObject.squarespaceOrderIds == null ? null : jsonSerializedObject.squarespaceOrderIds.map((v: any) => v);
          this.squarespaceOrderNumbers = jsonSerializedObject.squarespaceOrderNumbers == null ? null : jsonSerializedObject.squarespaceOrderNumbers.map((v: any) => v);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.createdFromDate = this.createdFromDate == null ? null : this.createdFromDate.toString();
        toRet.createdToDate = this.createdToDate == null ? null : this.createdToDate.toString();
        toRet.squarespaceOrderIds = this.squarespaceOrderIds == null ? null : this.squarespaceOrderIds.map(v => v);
        toRet.squarespaceOrderNumbers = this.squarespaceOrderNumbers == null ? null : this.squarespaceOrderNumbers.map(v => v);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class SquarespaceOrderService extends ServiceBase {

  public async getSquarespaceOrdersByFilter(filter: SquarespaceOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredSquarespaceOrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredSquarespaceOrdersResult(response.results);;
    return this.makeJsonRequest("squarespaceOrder/v1/getSquarespaceOrdersByFilter", req, false, extractor, opts);
  };
}

export const squarespaceOrderService = new SquarespaceOrderService();